import {
  ArrowLongLeftIcon,
  ArrowLongRightIcon,
  ChevronDoubleLeftIcon,
} from '@heroicons/react/16/solid';
import React, { useId } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { cn } from '../../lib/classNameHelpers';
import { Button } from '../catalyst-ui-kit/button';
import { Loader } from '../Loader';
import { MultiSelect } from '../MultiSelect/MultiSelect';
import { Skeleton } from '../Skeleton/Skeleton';
import { Tooltip } from '../Tooltip/Tooltip';

type TableProps = React.HTMLAttributes<HTMLDivElement> & {
  ref?: React.Ref<HTMLDivElement | null>;
};

const Table: React.FC<TableProps> = ({ className, ref, ...props }) => (
  <div
    ref={ref}
    className="relative size-full table-auto overflow-x-auto text-xs [&:not(:hover)]:scrollbar-hide"
  >
    <table
      className={twMerge('w-full caption-bottom border-separate border-spacing-y-0.5', className)}
      {...props}
    />
  </div>
);
Table.displayName = 'Table';

type TableHeaderProps = React.HTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.Ref<HTMLTableSectionElement | null>;
};

const TableHeader: React.FC<TableHeaderProps> = ({ className, ref, ...props }) => (
  <thead
    ref={ref}
    className={twMerge(
      'sticky top-0 z-10 h-6 bg-backgroundPrimary py-1 text-3xs text-contentSecondary shadow-[-1px_0px_0_0_var(--background-backgroundBase)]',
      className
    )}
    {...props}
  />
);
TableHeader.displayName = 'TableHeader';

type TableBodyProps = React.HTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.Ref<HTMLTableSectionElement | null>;
};

const TableBody: React.FC<TableBodyProps> = ({ className, ref, ...props }) => (
  <tbody ref={ref} className={twMerge('[&_tr:last-child]:border-y-0', className)} {...props} />
);
TableBody.displayName = 'TableBody';

type TableFooterProps = React.HTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.Ref<HTMLTableSectionElement | null>;
};

const TableFooter: React.FC<TableFooterProps> = ({ className, ref, ...props }) => (
  <tfoot
    ref={ref}
    className={twMerge('border-t font-medium [&>tr]:last:border-b-0', className)}
    {...props}
  />
);
TableFooter.displayName = 'TableFooter';

type TableHeadRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
  ref?: React.Ref<HTMLTableRowElement | null>;
};

const TableHeadRow: React.FC<TableHeadRowProps> = ({ className, ref, ...props }) => (
  <tr ref={ref} className={className} {...props} />
);
TableHeadRow.displayName = 'TableHeadRow';

type TableRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
  ref?: React.Ref<HTMLTableRowElement | null>;
};

const TableRow: React.FC<TableRowProps> = ({ className, ref, ...props }) => (
  <tr
    ref={ref}
    className={twMerge(
      'p-0 transition-colors data-[state=selected]:bg-backgroundPrimary',
      props.onClick && 'cursor-pointer hover:bg-backgroundBase',
      className
    )}
    {...props}
  />
);
TableRow.displayName = 'TableRow';

type TableHeadProps = React.ThHTMLAttributes<HTMLTableCellElement> & {
  ref?: React.Ref<HTMLTableCellElement | null>;
};

const TableHead: React.FC<TableHeadProps> = ({ className, ref, ...props }) => (
  <th
    ref={ref}
    className={twMerge(
      'text-nowrap px-4 py-0.5 text-left align-middle font-medium [&:has([role=checkbox])]:pr-0',
      className
    )}
    {...props}
  />
);
TableHead.displayName = 'TableHead';

type TableCellProps = React.TdHTMLAttributes<HTMLTableCellElement> & {
  ref?: React.Ref<HTMLTableCellElement | null>;
};

const TableCell: React.FC<TableCellProps> = ({ className, ref, ...props }) => (
  <td
    ref={ref}
    className={twMerge('px-4 py-1 align-middle text-xs [&:has([role=checkbox])]:pr-0', className)}
    {...props}
  />
);
TableCell.displayName = 'TableCell';

type TableCaptionProps = React.HTMLAttributes<HTMLTableCaptionElement> & {
  ref?: React.Ref<HTMLTableCaptionElement | null>;
};

const TableCaption: React.FC<TableCaptionProps> = ({ className, ref, ...props }) => (
  <caption ref={ref} className={twMerge('mt-4 text-xs', className)} {...props} />
);
TableCaption.displayName = 'TableCaption';

type SkeletonCellProps = {
  isAction?: boolean;
  className?: string;
};

const SkeletonCell: React.FC<SkeletonCellProps> = ({ isAction, className }) => (
  <TableCell className={className}>
    <Skeleton className={cn('h-4', isAction ? 'w-16 bg-backgroundBlue' : 'w-24')} />
  </TableCell>
);
SkeletonCell.displayName = 'SkeletonCell';

type SkeletonRowProps = {
  columnCount: number;
  actionColumnIndex?: number;
  skeletonRowClassName?: string;
};

const SkeletonRow: React.FC<SkeletonRowProps> = ({
  columnCount,
  actionColumnIndex,
  skeletonRowClassName,
}) => (
  <TableRow className={cn('h-8', skeletonRowClassName)}>
    {Array.from({ length: columnCount }).map((_, index) => (
      <SkeletonCell
        key={`skeleton-cell-${index}`}
        isAction={index === actionColumnIndex}
        className={index === columnCount - 1 ? 'flex justify-end' : undefined}
      />
    ))}
  </TableRow>
);
SkeletonRow.displayName = 'SkeletonRow';

type PaginationProps = {
  onFirst: () => void;
  onPrevious: () => void;
  onNext: () => void;
  currentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isLoading?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  setPageSize?: (pageSize: number) => void;

  className?: string;
};

export const Pagination: React.FC<PaginationProps> = ({
  onFirst,
  onPrevious,
  onNext,
  currentPage,
  hasNextPage,
  hasPreviousPage,
  isLoading = false,
  pageSize,
  pageSizeOptions,
  setPageSize,
  className,
}) => {
  const showRowsLabelId = useId();
  return (
    <div className={twMerge('flex items-center justify-between p-2 text-xs', className)}>
      <div>
        {pageSizeOptions && pageSizeOptions.length > 1 && setPageSize ? (
          <div className="flex items-center justify-center gap-1">
            <label id={showRowsLabelId} className="text-xs font-medium">
              Show Rows
            </label>
            <MultiSelect
              options={pageSizeOptions.map((value) => ({ value, label: String(value) }))}
              multiselect={false}
              value={pageSize}
              onChange={(value) => {
                if (value !== undefined) setPageSize(value);
              }}
              buttonClassName="!px-2 !py-1 !text-xs"
              panelOffset={2}
              triggerLabel="Show Rows"
              aria-labelledby={showRowsLabelId}
            />
          </div>
        ) : null}
      </div>
      <div className="flex items-center justify-between">
        <Tooltip content={hasPreviousPage ? 'Jump to first page' : undefined}>
          <Button
            plain
            className={twMerge(
              'inline-flex items-center rounded !px-2 !py-1 !text-xs transition-opacity duration-100 disabled:opacity-50',
              !hasPreviousPage ? 'duration-0' : 'duration-100',
              currentPage <= 1 ? '!opacity-0 duration-200' : ''
            )}
            onClick={onFirst}
            disabled={!hasPreviousPage}
            aria-label="Go to first page"
          >
            <ChevronDoubleLeftIcon className="size-4 fill-contentPrimary" aria-hidden="true" />
            <span className="sr-only">Go to first page</span>
          </Button>
        </Tooltip>
        <Button
          plain
          className={twMerge(
            'inline-flex items-center rounded !px-2 !py-1 !text-xs transition-opacity duration-100 disabled:opacity-50',
            !hasPreviousPage ? 'duration-0' : 'duration-100'
          )}
          onClick={onPrevious}
          disabled={!hasPreviousPage}
          aria-label="Go to previous page"
        >
          <ArrowLongLeftIcon className="mr-1 size-4 fill-contentPrimary" aria-hidden="true" />
          <span>Previous</span>
        </Button>

        <div
          className={twJoin(
            'relative flex size-6 items-center justify-center rounded transition-opacity duration-100',
            currentPage > 0 ? 'bg-backgroundPrimary opacity-100' : 'opacity-0'
          )}
        >
          <span
            className={twJoin(
              'cursor-default font-bold text-contentPrimary transition-opacity duration-100'
            )}
            aria-label={`Page ${currentPage + 1}`}
            aria-live="polite"
            aria-atomic="true"
          >
            {/* Avoid switching to '1' while fading out */}
            {Math.max(currentPage + 1, 2)}
          </span>
        </div>

        <Button
          plain
          className={twJoin(
            'group inline-flex items-center rounded !px-2 !py-1 !text-xs text-contentPrimary transition-opacity disabled:opacity-50',
            !hasNextPage ? 'duration-0' : 'duration-100',
            isLoading ? 'disabled:!opacity-100' : ''
          )}
          onClick={onNext}
          disabled={!hasNextPage}
          aria-label="Go to next page"
          aria-busy={isLoading}
        >
          <span className={isLoading ? 'opacity-20' : ''}>Next</span>
          <ArrowLongRightIcon
            className={twJoin('ml-1 size-4 fill-contentPrimary', isLoading ? 'opacity-20' : '')}
            aria-hidden="true"
          />
          <Loader
            className={twJoin(
              'absolute transition-opacity duration-100',
              isLoading ? 'opacity-100' : 'opacity-0'
            )}
            aria-hidden={!isLoading}
          />
        </Button>
      </div>
    </div>
  );
};

export {
  SkeletonCell,
  SkeletonRow,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableHeadRow,
  TableRow,
};
